import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { combineLatest, map, Observable, startWith, tap } from 'rxjs';

import { DiscussionFormGroup, DiscussionFormValue } from '../../models/form/discussion-form.model';

interface DiscussionStepperState {
  /** Current page/section number */
  current: number;

  /** Total number of pages/sections */
  total: number;
}

enum ManageeSections {
  CoreValues = 'CoreValues',
  RoleRatings = 'RoleRatings',
  PerfData = 'PerformanceData',
  CustomQuestions = 'CustomQuestions',
}

enum ManagerSections {
  Leadership = 'LeadershipCommitments',
  Coaching = 'CoachingCommitments',
}

type Section = ManagerSections | ManageeSections;

type StoreConfig = {
  displayManageeFormSection: boolean;
  displayManagerFormSection: boolean;
  questions: unknown[];
  form: DiscussionFormGroup;
};

type SectionToFormKeyMap = Partial<Record<Section, keyof DiscussionFormValue>>;

@Injectable()
export class DiscussionStepperComponentStore extends ComponentStore<DiscussionStepperState> {
  constructor() {
    super({ current: 1, total: 1 });
  }

  /** Maps page numbers to a form section */
  private sectionMap = new Map<number, Section>();

  /** Maps form sections to form control keys */
  readonly sectionToFormKeyMap: SectionToFormKeyMap = {
    [ManageeSections.CoreValues]: 'coreValues',
    [ManageeSections.RoleRatings]: 'roleRatings',
    [ManageeSections.CustomQuestions]: 'customQuestions',
    [ManagerSections.Leadership]: 'leadership',
    [ManagerSections.Coaching]: 'coaching',
  };

  /** Current page/section number */
  readonly current$: Observable<number> = this.select(state => state.current);

  /** Total number of pages/sections */
  readonly sectionCount$: Observable<number> = this.select(state => state.total);

  readonly currentSection$ = this.select(state => this.sectionMap.get(state.current) ?? ManageeSections.CoreValues);

  readonly isManagerSection$ = this.currentSection$.pipe(
    map(current => {
      const managerSects = [ManagerSections.Leadership, ManagerSections.Coaching];
      return managerSects.includes(current as ManagerSections);
    })
  );

  readonly ManageeSections = ManageeSections;
  readonly ManagerSections = ManagerSections;

  readonly onBack = this.updater((state): DiscussionStepperState => ({
    ...state,
    current: ((state.current - 2 + state.total) % state.total) + 1,
  }));

  readonly onNext = this.updater((state): DiscussionStepperState => ({
    ...state,
    current: (state.current % state.total) + 1,
  }));

  readonly onSetCurrent = this.updater((state, current: number): DiscussionStepperState => ({
    ...state,
    current,
  }));

  readonly onTotalChanged = this.updater((state, total: number): DiscussionStepperState => ({
    ...state,
    total,
  }));

  /** Determines how many sections will be displayed based on the state of the given Discussion. */
  readonly init = this.effect((config$: Observable<StoreConfig>) =>
    config$.pipe(
      tap(({ displayManageeFormSection, displayManagerFormSection, form, questions }) => {
        this.sectionMap = new Map<number, Section>();

        const addSection = (section: Section) => {
          this.sectionMap.set(this.sectionMap.size + 1, section);
        };

        if (displayManageeFormSection) {
          addSection(ManageeSections.CoreValues);
          addSection(ManageeSections.RoleRatings);
          addSection(ManageeSections.PerfData);
        }

        if (displayManageeFormSection && questions.length) {
          addSection(ManageeSections.CustomQuestions);
        }

        if (displayManagerFormSection) {
          addSection(ManagerSections.Leadership);
          addSection(ManagerSections.Coaching);
        }

        this.onTotalChanged(this.sectionMap.size);

        this.startUserOnFirstSectionWithError(form);
      })
    )
  );

  getTotalQuestionsForCurrentSection(form: DiscussionFormGroup, questions: unknown[]) {
    return this.currentSection$.pipe(
      map(section => {
        if (section === ManageeSections.CustomQuestions) return questions.length;

        const formKey = this.sectionToFormKeyMap[section];

        if (!formKey) return 0;

        const questionsBySection: Record<keyof DiscussionFormValue, number> = {
          coreValues: form.value?.coreValues?.manager.ratings.length ?? 0,
          roleRatings: form.value?.roleRatings?.manager.ratings.length ?? 0,
          leadership: form.value?.leadership?.manager.ratings.length ?? 0,
          coaching: form.value?.coaching?.manager.ratings.length ?? 0,
          customQuestions: questions.length ?? 0,
        };

        return questionsBySection[formKey] ?? 0;
      })
    );
  }

  trackErrorsForCurrentSection(form: DiscussionFormGroup) {
    const formValueChanges$ = form.valueChanges.pipe(startWith(form.value));
    return combineLatest([this.currentSection$, formValueChanges$]).pipe(
      map(([currentSection]) => {
        const formKey = this.sectionToFormKeyMap[currentSection];
        const errors = form.errors;

        if (formKey && errors?.[formKey]?.value) {
          return errors[formKey]?.value.length;
        }
        return 0;
      })
    );
  }

  private startUserOnFirstSectionWithError(form: DiscussionFormGroup) {
    if (!form.errors) return;
    for (const [key, val] of this.sectionMap.entries()) {
      const errorKey = this.sectionToFormKeyMap[val];
      if (errorKey && form.errors[errorKey]) {
        this.onSetCurrent(key);
        break;
      }
    }
  }
}
